import { ref } from "vue";
import StorageService from "@/services/storage.service";
import ApiService from "@/services/api.service";
import Clarity from "@microsoft/clarity";
import useAuth from "@/componentables/auth";

export type ConsentStatus = "granted" | "denied" | null;

const STORAGE_KEY = "analytics_consent";

const consentStatus = ref<ConsentStatus>(null);
const isLoaded = ref(false);

export function useConsent() {
    async function loadConsent(): Promise<ConsentStatus> {
        const stored = await StorageService.get(STORAGE_KEY);
        consentStatus.value =
            stored === "granted" || stored === "denied" ? stored : null;
        isLoaded.value = true;

        if (consentStatus.value) {
            applyClarityConsent(consentStatus.value);
        }

        return consentStatus.value;
    }

    /**
     * Pull consent from the API and apply it locally.
     * Call this after the user has logged in to sync cross-device preferences.
     */
    async function syncConsentFromApi(): Promise<void> {
        try {
            const auth = useAuth();
            if (!(await auth.isAuthenticated())) {
                return;
            }

            const response = await ApiService.get("/profile/settings");
            const settings = response.data.data || response.data;
            const apiConsent =
                settings?.privacy?.children?.analytics_consent?.value ??
                settings?.privacy?.analytics_consent?.value ??
                null;

            if (apiConsent === null || apiConsent === undefined) {
                if (consentStatus.value !== null) {
                    syncConsentToApi(consentStatus.value === "granted");
                }
                return;
            }

            const newStatus: ConsentStatus = apiConsent ? "granted" : "denied";

            if (consentStatus.value === null) {
                consentStatus.value = newStatus;
                await StorageService.set(STORAGE_KEY, newStatus);
                applyClarityConsent(newStatus);
            }
        } catch {
            // Silently fail -- local storage is the fallback
        }
    }

    function applyClarityConsent(status: ConsentStatus): void {
        if (!status) {
            return;
        }

        Clarity.consentV2({
            ad_Storage: status,
            analytics_Storage: status,
        });
    }

    async function syncConsentToApi(granted: boolean): Promise<void> {
        try {
            const auth = useAuth();
            if (!(await auth.isAuthenticated())) {
                return;
            }

            await ApiService.put("/profile/settings", {
                settings: {
                    analytics_consent: granted,
                },
            });
        } catch {
            // Silently fail -- local storage is the primary source
        }
    }

    async function grantConsent(): Promise<void> {
        consentStatus.value = "granted";
        await StorageService.set(STORAGE_KEY, "granted");
        applyClarityConsent("granted");
        syncConsentToApi(true);
    }

    async function denyConsent(): Promise<void> {
        consentStatus.value = "denied";
        await StorageService.set(STORAGE_KEY, "denied");
        applyClarityConsent("denied");
        syncConsentToApi(false);
    }

    async function resetConsent(): Promise<void> {
        consentStatus.value = null;
        await StorageService.remove(STORAGE_KEY);
    }

    return {
        consentStatus,
        isLoaded,
        loadConsent,
        syncConsentFromApi,
        grantConsent,
        denyConsent,
        resetConsent,
    };
}
